import { useEffect, useId, useRef, useState } from "react";
import type { Map as LeafletMap, Marker } from "leaflet";
import { Icon } from "./MarketplaceUI.js";
import { locationPoint, roundedLocation, type LocationPoint } from "./location-point.js";
import "./location-picker.css";

type LocationSource = "manual" | "user_permission";
interface PickerProps {
  value: LocationPoint | null;
  onChange: (point: LocationPoint, source: LocationSource) => void;
  disabled?: boolean;
  initiallyOpen?: boolean;
  precision?: 3 | 6;
}

function InteractiveMap({ value, onChange, disabled = false, precision = 6 }: PickerProps) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<LeafletMap | null>(null);
  const marker = useRef<Marker | null>(null);
  const synchronize = useRef<((point: LocationPoint | null) => void) | null>(null);
  const current = useRef({ value, onChange, disabled, precision });
  const [ready, setReady] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [tileFailure, setTileFailure] = useState(false);
  const [retry, setRetry] = useState(0);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const helpId = useId();

  useEffect(() => { current.current = { value, onChange, disabled, precision }; }, [value, onChange, disabled, precision]);
  useEffect(() => {
    let active = true;
    let instance: LeafletMap | null = null;
    let resize: ResizeObserver | null = null;
    let tileTimeout: ReturnType<typeof setTimeout> | undefined;
    setReady(false); setFailure(null); setTileFailure(false);
    void Promise.all([import("leaflet"), import("leaflet/dist/leaflet.css")]).then(([L]) => {
      if (!active || !container.current) return;
      instance = L.map(container.current, {
        scrollWheelZoom: false, zoomControl: false, minZoom: 5, maxZoom: 19,
        maxBounds: [[-28.3, -63.4], [-18.6, -53.4]], maxBoundsViscosity: 1
      });
      map.current = instance;
      const selected = current.current.value;
      instance.setView(selected ? [selected.lat, selected.lng] : [-23.45, -58.4], selected ? 16 : 6);
      L.control.zoom({ zoomInTitle: "Acercar mapa", zoomOutTitle: "Alejar mapa" }).addTo(instance);
      instance.attributionControl.setPrefix('<a href="https://leafletjs.com" target="_blank" rel="noopener">Leaflet</a>');
      const tiles = L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19, updateWhenIdle: true, updateWhenZooming: false, keepBuffer: 1,
        referrerPolicy: "strict-origin-when-cross-origin",
        attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors'
      });
      const failedTiles = new Set<HTMLElement>();
      tiles.on("tileerror", (event) => { failedTiles.add(event.tile); if (active) setTileFailure(true); });
      tiles.on("tileunload", (event) => { failedTiles.delete(event.tile); });
      tiles.on("load", () => { clearTimeout(tileTimeout); if (active) setTileFailure(failedTiles.size > 0); });
      tiles.addTo(instance);
      tileTimeout = setTimeout(() => { if (active && tiles.isLoading()) setTileFailure(true); }, 15000);
      const icon = L.divIcon({ className: "pp-map-marker", html: '<span aria-hidden="true"></span>', iconSize: [44, 50], iconAnchor: [22, 46] });
      const place = (point: LocationPoint | null) => {
        if (!instance) return;
        if (!point) { marker.current?.remove(); marker.current = null; return; }
        if (!marker.current) {
          marker.current = L.marker([point.lat, point.lng], { icon, draggable: !current.current.disabled, title: "Ubicación elegida: arrastrá para ajustar", keyboard: true, autoPan: true }).addTo(instance);
          marker.current.on("dragend", () => {
            const coordinates = marker.current?.getLatLng();
            if (coordinates) choose(coordinates.lat, coordinates.lng);
          });
        } else marker.current.setLatLng([point.lat, point.lng]);
        if (!instance.getBounds().pad(-0.15).contains([point.lat, point.lng])) instance.panTo([point.lat, point.lng], { animate: false });
      };
      const choose = (lat: number, lng: number) => {
        if (current.current.disabled) { place(current.current.value); return; }
        const point = locationPoint(lat, lng);
        if (!point) { setSelectionError("Elegí un punto dentro del área de Paraguay."); place(current.current.value); return; }
        setSelectionError(null);
        current.current.onChange(roundedLocation(point, current.current.precision), "manual");
      };
      instance.on("click", (event) => choose(event.latlng.lat, event.latlng.lng));
      synchronize.current = place;
      place(current.current.value);
      resize = new ResizeObserver(() => instance?.invalidateSize({ pan: false }));
      resize.observe(container.current);
      instance.invalidateSize({ pan: false });
      setReady(true);
    }).catch(() => { if (active) setFailure("No pudimos abrir el mapa. Podés usar el dispositivo o ingresar las coordenadas y reintentar."); });
    return () => {
      active = false; clearTimeout(tileTimeout); resize?.disconnect();
      instance?.remove(); map.current = null; marker.current = null; synchronize.current = null;
    };
  }, [retry]);
  useEffect(() => { synchronize.current?.(value); setSelectionError(null); }, [value?.lat, value?.lng]);
  useEffect(() => { if (disabled) marker.current?.dragging?.disable(); else marker.current?.dragging?.enable(); }, [disabled, ready]);

  const centerSelection = () => {
    const center = map.current?.getCenter();
    const point = center ? locationPoint(center.lat, center.lng) : null;
    if (!point) { setSelectionError("Mové el mapa hasta un punto dentro de Paraguay."); return; }
    setSelectionError(null); onChange(roundedLocation(point, precision), "manual");
  };
  return <div className="pp-map-editor">
    <p className="pp-map-instructions" id={helpId}><Icon name="pin" />Tocá el mapa para marcar el punto. Podés arrastrar el marcador o mover el mapa con las flechas del teclado.</p>
    <div className="pp-map-frame"><div className="pp-map-canvas" ref={container} role="region" aria-label="Mapa para elegir ubicación" aria-describedby={helpId} tabIndex={0} />{!ready && <div className="pp-map-loading" role="status">{failure ?? "Cargando mapa…"}</div>}<span className="pp-map-crosshair" aria-hidden="true">+</span></div>
    {(failure || tileFailure) && <div className="pp-map-feedback" role="status"><span>{failure ?? "Algunas partes del mapa no cargaron. Revisá tu conexión; la ubicación elegida se conserva."}</span><button type="button" onClick={() => setRetry(retry + 1)}>Reintentar mapa</button></div>}
    <div className="pp-map-tools"><button type="button" disabled={!ready || disabled} onClick={centerSelection}>Marcar el centro del mapa</button><button type="button" disabled={!ready || !value} onClick={() => { if (value) map.current?.setView([value.lat, value.lng], 17, { animate: false }); }}>Ver punto elegido</button></div>
    {selectionError && <p className="pp-location-error" role="alert">{selectionError}</p>}
    <a className="pp-map-report" href="https://www.openstreetmap.org/fixthemap" target="_blank" rel="noopener noreferrer">Informar un error del mapa</a>
  </div>;
}

export default function LocationPicker({ value, onChange, disabled = false, initiallyOpen = false, precision = 6 }: PickerProps) {
  const [open, setOpen] = useState(initiallyOpen);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  const mapId = useId();
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const locate = () => {
    setError(null);
    if (!navigator.geolocation) { setError("Este navegador no ofrece ubicación. Podés elegir el punto en el mapa."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition((position) => {
      if (!alive.current) return;
      const point = locationPoint(position.coords.latitude, position.coords.longitude);
      if (point) onChange(roundedLocation(point, precision), "user_permission");
      else setError("El dispositivo devolvió una ubicación fuera del área de Paraguay. Elegí el punto en el mapa.");
      setLocating(false);
    }, () => { if (alive.current) { setError("No pudimos obtener la ubicación. Revisá el permiso o elegí un punto en el mapa."); setLocating(false); } }, { enableHighAccuracy: precision === 6, timeout: 12000, maximumAge: 60000 });
  };
  return <section className="pp-location-picker" aria-label="Opciones de ubicación">
    <div className="pp-location-choices"><button type="button" className={open ? "selected" : ""} aria-expanded={open} aria-controls={mapId} onClick={() => setOpen(!open)}><Icon name="pin" /><span>{open ? "Ocultar mapa" : "Elegir ubicación en el mapa"}</span></button><button type="button" onClick={locate} disabled={locating || disabled}><span className="pp-gps-icon" aria-hidden="true">⌖</span><span>{locating ? "Obteniendo ubicación…" : "Usar la ubicación de este dispositivo"}</span></button></div>
    <p className="pp-location-privacy">El GPS se consulta solo al presionar su botón. Al abrir el mapa se cargan imágenes de OpenStreetMap; no se comparte tu cuenta ni se activa seguimiento.</p>
    {error && <p className="pp-location-error" role="alert">{error}</p>}
    {open && <div id={mapId}><InteractiveMap value={value} onChange={onChange} disabled={disabled || locating} precision={precision} /></div>}
    <div className="pp-location-summary" role="status"><Icon name={value ? "check" : "pin"} /><span>{value ? `Punto elegido: ${value.lat.toFixed(precision)}, ${value.lng.toFixed(precision)}` : "Todavía no elegiste un punto. Abrir el mapa no asigna una ubicación."}</span></div>
  </section>;
}
